import React from "react";
import PageTitle from "@Components/PageTitle/PageTitle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import themeCreator from "@Themes/themeCreator";
import {
  Container,
  Tabs,
  Tab,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Stack,
  Collapse,
} from "@mui/material";
import { Box } from "@mui/system";
import { ListingPageContainer } from "../ListingPageTemplate/ListingPageTemplateStyles";
import NotificationTile from "./NotificationTile";
import { useEffect, useState } from "react";
import { NOTIFICATIONS_URL } from "@API/APIRoutes";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import usePostItem from "@Hooks/usePostItem";
import useDeleteItem from "@Hooks/useDeleteItem";
import {
  formatNotifications,
  formatDate,
} from "@Utils/Validations/HelperFunctions/FormaterFunctions";
import { encryptId } from "../../Utils/SecurityFunctions";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import IconTextButton from "@Components/Buttons/IconTextButton";
import usePaginate from "@Hooks/usePaginate";

export default function Notification() {
  const notificationFilterOptions = [
    { value: "poNumber", label: "Po Number" },
    { value: "acidNumber", label: "Acid Number" },
  ];
  const notificationOperatorOptions = [
    { value: "contains", label: "contains" },
    { value: "equals", label: "equals" },
  ];
  const theme = themeCreator();
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const { loading: postLoading, postItem } = usePostItem();
  const { loading: deleteLoading, deleteItem } = useDeleteItem();
  const navigate = useNavigate();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [showDeleteAllPopup, setShowDeleteAllPopup] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    unread: 0,
    read: 0,
  });
  const [selectedFilter, setSelectedFilter] = useState("poNumber");
  const [selectedOperator, setSelectedOperator] = useState("contains");
  const [filterValue, setFilterValue] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const { getPage } = usePaginate();
  const hasAppliedFilter = appliedFilters.length > 0;

  const getStatusFilter = (selectedTab = tabValue) => {
    if (selectedTab === 1) {
      return { field: "read", operator: "equals", value: false };
    }
    if (selectedTab === 2) {
      return { field: "read", operator: "equals", value: true };
    }
    return null;
  };

  const buildFiltersForTab = (
    selectedTab = tabValue,
    baseFilters = appliedFilters
  ) => {
    const filters = [...baseFilters];
    const statusFilter = getStatusFilter(selectedTab);

    if (statusFilter) {
      filters.push(statusFilter);
    }

    return filters;
  };

  const viewOrder = (notificationId, orderId) => {
    postItem(
      `${NOTIFICATIONS_URL}/${notificationId}/read`,
      {},
      () => {
        fetchNotifications(page, rowsPerPage, buildFiltersForTab());
        fetchTabCounts(appliedFilters);
      },
      "",
      "Failed to mark notification as read",
      false
    );
    navigate(DefaultRoutes.orderInfo.replace(":orderId", encryptId(orderId)));
  };

  const onReadOrder = (notificationId) => {
    postItem(
      `${NOTIFICATIONS_URL}/${notificationId}/read`,
      {},
      () => {
        fetchNotifications(page, rowsPerPage, buildFiltersForTab());
        fetchTabCounts(appliedFilters);
      },
      "",
      "Failed to mark notification as read",
      false
    );
  };

  const handleReadAll = () => {
    postItem(
      `${NOTIFICATIONS_URL}/read-all`,
      {},
      () => {
        fetchNotifications(page, rowsPerPage, buildFiltersForTab());
        fetchTabCounts(appliedFilters);
      },
      "",
      "Failed to mark all notifications as read",
      false
    );
  };

  const handleTabChange = (event, newValue) => {
    setPage(0);
    setTabValue(newValue);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setNotificationToDelete(null);
  };

  const openDeletePopup = (notificationId) => {
    setNotificationToDelete(notificationId);
    setShowDeletePopup(true);
  };

  const openDeleteAllPopup = () => {
    setShowDeleteAllPopup(true);
  };

  const closeDeleteAllPopup = () => {
    setShowDeleteAllPopup(false);
  };

  const handleDeleteNotification = (notificationId) => {
    if (!notificationId) return;
    deleteItem(
      notificationId,
      (deletedId) => {
        setNotifications((prev) =>
          prev.filter((notification) => notification.id !== deletedId)
        );
        setTotalElements((prev) => Math.max(prev - 1, 0));
        fetchTabCounts(appliedFilters);
        closeDeletePopup();
      },
      NOTIFICATIONS_URL,
      "Notification deleted successfully",
      "Failed to delete notification"
    );
  };

  const handleDeleteAllNotifications = () => {
    deleteItem(
      null,
      () => {
        setNotifications([]);
        setTotalElements(0);
        setTotalPages(1);
        setPage(0);
        fetchTabCounts(appliedFilters);
        closeDeleteAllPopup();
      },
      NOTIFICATIONS_URL,
      "Notifications deleted successfully",
      "Failed to delete notifications"
    );
  };

  const fetchNotifications = async (
    pageNumber = page,
    pageSize = rowsPerPage,
    filters = buildFiltersForTab()
  ) => {
    setLoadingNotifications(true);
    try {
      const queryOptions = {
        page: pageNumber,
        size: pageSize,
        sortBy: "createdAt",
        sortDir: "desc",
      };
      const response = await getPage(
        NOTIFICATIONS_URL,
        "Failed to get notifications",
        queryOptions,
        filters
      );

      if (response) {
        const formattedResponse = formatNotifications(response.content || []);
        setNotifications(formattedResponse);
        setTotalPages(response.totalPages || 1);
        setTotalElements(response.totalElements || 0);
      }
    } finally {
      setLoadingNotifications(false);
    }
  };

  const fetchTabCounts = async (baseFilters = appliedFilters) => {
    const countQueryOptions = {
      page: 0,
      size: 1,
      sortBy: "createdAt",
      sortDir: "desc",
    };

    const [allResponse, unreadResponse, readResponse] = await Promise.all([
      getPage(
        NOTIFICATIONS_URL,
        "Failed to get notifications",
        countQueryOptions,
        buildFiltersForTab(0, baseFilters)
      ),
      getPage(
        NOTIFICATIONS_URL,
        "Failed to get notifications",
        countQueryOptions,
        buildFiltersForTab(1, baseFilters)
      ),
      getPage(
        NOTIFICATIONS_URL,
        "Failed to get notifications",
        countQueryOptions,
        buildFiltersForTab(2, baseFilters)
      ),
    ]);

    setTabCounts({
      all: allResponse?.totalElements || 0,
      unread: unreadResponse?.totalElements || 0,
      read: readResponse?.totalElements || 0,
    });
  };

  useEffect(() => {
    fetchNotifications(page, rowsPerPage, buildFiltersForTab());
  }, [page, rowsPerPage, appliedFilters, tabValue]);

  useEffect(() => {
    fetchTabCounts(appliedFilters);
  }, [appliedFilters]);

  const handlePageChange = (event, value) => {
    setPage(value - 1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
    setFilterValue("");
  };

  const applyNotificationFilter = () => {
    const trimmedValue = filterValue.trim();
    const newFilters = trimmedValue
      ? [{ field: selectedFilter, operator: selectedOperator, value: trimmedValue }]
      : [];

    setPage(0);
    setAppliedFilters(newFilters);
  };

  const clearNotificationFilter = () => {
    setFilterValue("");
    setPage(0);
    setAppliedFilters([]);
  };

  if (loadingNotifications) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <ListingPageContainer className="listingPageContainer">
      <Container
        sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
        disableGutters
      >
        <Box className="LabelButtonBox">
          <PageTitle
            title="Notifications"
            PageIcon={<NotificationsIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          ></PageTitle>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`All (${tabCounts.all || 0})`} />
              <Tab label={`Unread (${tabCounts.unread || 0})`} />
              <Tab label={`read (${tabCounts.read || 0})`} />
            </Tabs>

            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.5}
              sx={{ width: { xs: "100%", md: "auto" }, alignItems: { xs: "stretch", lg: "center" } }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <IconTextButton
                  buttonText={isFilterPanelOpen ? "Hide Filters" : "Filters"}
                  Icon={FilterListIcon}
                  onClick={() => setIsFilterPanelOpen((prev) => !prev)}
                  sx={{ height: 40 }}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="rows-per-page-label">Rows</InputLabel>
                  <Select
                    labelId="rows-per-page-label"
                    value={rowsPerPage}
                    label="Rows"
                    onChange={handleRowsPerPageChange}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Pagination
                  count={Math.max(totalPages, 1)}
                  page={page + 1}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReadAll}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2.5,
                    py: 0.5,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "primary.main",
                      borderColor: "primary.main",
                      color: "#ffffff !important",
                      transform: "scale(1.05)",
                      boxShadow: 2,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "grey.100",
                      color: "grey.400",
                      borderColor: "grey.200",
                    },
                  }}
                >
                  {!postLoading ? (
                    "Read All"
                  ) : (
                    <LoadingComponent size="2em" thickness={5} color="blue" />
                  )}
                </Button>
                <Tooltip title="Delete all notifications">
                  <span>
                    <IconButton
                      onClick={openDeleteAllPopup}
                      sx={{
                        color: "error.main",
                        backgroundColor: "rgba(211, 47, 47, 0.1)",
                        "&:hover": {
                          backgroundColor: "rgba(211, 47, 47, 0.2)",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </Box>

          <Collapse in={isFilterPanelOpen}>
            <Box
              sx={{
                pt: 2,
                pb: 1,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="notification-filter-label">Filter By</InputLabel>
                <Select
                  labelId="notification-filter-label"
                  value={selectedFilter}
                  label="Filter By"
                  onChange={handleFilterChange}
                >
                  {notificationFilterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="notification-operator-label">Operator</InputLabel>
                <Select
                  labelId="notification-operator-label"
                  value={selectedOperator}
                  label="Operator"
                  onChange={(event) => setSelectedOperator(event.target.value)}
                >
                  {notificationOperatorOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Value"
                value={filterValue}
                onChange={(event) => setFilterValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    applyNotificationFilter();
                  }
                }}
                sx={{ minWidth: 220 }}
              />
              {hasAppliedFilter && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearNotificationFilter}
                  sx={{ height: 40, textTransform: "none" }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Collapse>
        </Box>

        <Box
          sx={{
            marginTop: 3,
            overflowY: "auto",
            maxHeight: "calc(100vh - 280px)",
            paddingRight: "8px",
          }}
        >
          {notifications?.length === 0 ? (
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "#757575",
                marginTop: "20px",
              }}
            >
              There are no notifications
            </Typography>
          ) : (
            notifications?.map((notification) => (
              <NotificationTile
                key={notification.id}
                title={notification.subject}
                message={notification.message}
                senderName={notification.senderUserName}
                date={formatDate(notification.createdAt)}
                isRead={notification.read}
                theme={theme}
                onViewFullOrder={() =>
                  viewOrder(notification.id, notification.orderId)
                }
                onReadOrder={() => onReadOrder(notification.id)}
                onDelete={() => openDeletePopup(notification.id)}
              />
            ))
          )}
        </Box>
        {DeletePopup(
          "notification",
          showDeletePopup,
          closeDeletePopup,
          handleDeleteNotification,
          notificationToDelete,
          deleteLoading
        )}
        {DeletePopup(
          "notifications",
          showDeleteAllPopup,
          closeDeleteAllPopup,
          handleDeleteAllNotifications,
          null,
          deleteLoading,
          "Delete All"
        )}
      </Container>
    </ListingPageContainer>
  );
}
